import { MAX_SCREENSHOT_BYTES, PNG_DATA_URL_PREFIX } from './constants';

/**
 * Class applied to the feedback dialog (and its nested enlarge view) so
 * html2canvas skips them: the screenshot must show the app, not the form.
 */
export const FEEDBACK_CAPTURE_IGNORE_CLASS = 'feedback-capture-ignore';

/** Each downscale step multiplies both canvas dimensions by this factor. */
const DOWNSCALE_FACTOR = 0.85;

/** A canvas cannot shrink below a single pixel per side. */
const MIN_CANVAS_DIMENSION = 1;

/** Base64 packs 3 bytes into every 4 characters. */
const BASE64_BYTES_PER_GROUP = 3;
const BASE64_CHARS_PER_GROUP = 4;

function decodedByteLength(dataUrl: string): number {
  const base64 = dataUrl.slice(PNG_DATA_URL_PREFIX.length);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * BASE64_BYTES_PER_GROUP) / BASE64_CHARS_PER_GROUP) - padding;
}

function downscale(source: HTMLCanvasElement): HTMLCanvasElement {
  const width = Math.max(Math.floor(source.width * DOWNSCALE_FACTOR), MIN_CANVAS_DIMENSION);
  const height = Math.max(Math.floor(source.height * DOWNSCALE_FACTOR), MIN_CANVAS_DIMENSION);
  const scaled = document.createElement('canvas');
  scaled.width = width;
  scaled.height = height;
  const ctx = scaled.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create canvas context for screenshot downscale');
  }
  ctx.drawImage(source, 0, 0, width, height);
  return scaled;
}

/**
 * Capture everything visible in the browser tab (sized to the viewport) as a
 * PNG data URL. The WebGL map canvases run with `preserveDrawingBuffer: true`
 * so their buffers are readable, but html2canvas draws secondary canvases
 * unreliably -- so every canvas in the clone is replaced with an `<img>`
 * snapshot taken from its live counterpart before rendering. The output is
 * downscaled until it fits the server's screenshot byte cap.
 */
export async function captureAppScreenshot(): Promise<string> {
  const { default: html2canvas } = await import('html2canvas-pro');
  const captured = await html2canvas(document.body, {
    width: window.innerWidth,
    height: window.innerHeight,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    scrollX: 0,
    scrollY: -window.scrollY,
    scale: 1,
    useCORS: true,
    logging: false,
    backgroundColor: null,
    ignoreElements: element => element.classList.contains(FEEDBACK_CAPTURE_IGNORE_CLASS),
    onclone: clonedDoc => {
      const sources = Array.from(document.querySelectorAll('canvas')).filter(
        canvas => !canvas.closest(`.${FEEDBACK_CAPTURE_IGNORE_CLASS}`)
      );
      const clones = Array.from(clonedDoc.querySelectorAll('canvas')).filter(
        canvas => !canvas.closest(`.${FEEDBACK_CAPTURE_IGNORE_CLASS}`)
      );
      sources.forEach((source, index) => {
        const clone = clones[index];
        if (!clone?.parentElement) return;
        const snapshot = clonedDoc.createElement('img');
        snapshot.src = source.toDataURL('image/png');
        snapshot.className = clone.className;
        const inlineStyle = clone.getAttribute('style');
        if (inlineStyle) snapshot.setAttribute('style', inlineStyle);
        const rect = source.getBoundingClientRect();
        snapshot.style.width = `${rect.width}px`;
        snapshot.style.height = `${rect.height}px`;
        // deck.gl overlay canvases sit above the basemaps; keep that order
        // with a positive z-index so the negative-z-index workaround below
        // cannot invert it.
        if (source.id === 'deckgl-overlay') {
          snapshot.style.zIndex = '1';
        }
        clone.parentElement.replaceChild(snapshot, clone);
      });
      // react-map-gl gives maps nested under deck.gl `z-index: -1` so the
      // deck canvas paints above them. html2canvas drops nested content in
      // negative-z-index stacking layers, so the clone flattens to z-index 0
      // (with the deck overlay lifted to 1 above) to reproduce the same
      // paint order using only non-negative indices.
      clonedDoc.querySelectorAll<HTMLElement>('.maplibregl-map').forEach(map => {
        if (map.style.zIndex === '-1') {
          map.style.zIndex = '0';
        }
      });
    },
  });

  let canvas = captured;
  let dataUrl = canvas.toDataURL('image/png');
  while (decodedByteLength(dataUrl) > MAX_SCREENSHOT_BYTES) {
    const smaller = downscale(canvas);
    if (smaller.width === canvas.width && smaller.height === canvas.height) break;
    canvas = smaller;
    dataUrl = canvas.toDataURL('image/png');
  }
  return dataUrl;
}
